import { Fragment, useMemo } from 'react';

import { differenceInMinutes, format, subDays } from 'date-fns';
import { enUS as en, is } from 'date-fns/locale';

import { DatePicker } from '@components/datepicker';
import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconDate } from '@components/icons/date';
import { IconRadioChecked } from '@components/icons/radio-checked';
import { IconRadioIdle } from '@components/icons/radio-idle';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { AdminSiteLanguageType } from '@locale/locale-store';

import { useStore } from 'models/Provider';

import { FilterActionButtons } from './filter-action-buttons';

const LOCALE: Record<AdminSiteLanguageType, object> = { en, is };

const DATE_FIELDS = ['applied', 'hired', 'rejected'];
const ABSOLUTE_COMPARERS: Array<FilterComparer> = ['after', 'on', 'before'];
const RELATIVE_COMPARERS: Array<FilterComparer> = ['gt', 'eq', 'lt'];
const COMBINED_COMPARERS = [...ABSOLUTE_COMPARERS, ...RELATIVE_COMPARERS];

function FilterApplicationDatesApplied({ data }: FilterAppliedProps) {
  const { t, lang } = useStore();

  let value: string | undefined = undefined;
  if (!data.comparer) return null;

  if (ABSOLUTE_COMPARERS.includes(data.comparer)) {
    if (data.value) {
      value = format(new Date(data.value as string), 'do MMMM yyyy', {
        locale: LOCALE[lang as AdminSiteLanguageType],
      });
    }
  } else if (RELATIVE_COMPARERS.includes(data.comparer)) {
    value = `${data.value} ${t('filters.daysago')}`;
  }

  return (
    <Filters.AppliedFilter
      icon={<IconDate />}
      category={t('applicants.filters.date.' + data.field)}
      value={value}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationDatesMenu() {
  const { t, lang } = useStore();
  const { state, actions, hasValue } = useCurrentFilter();

  let disableApply = true;

  if (state.comparer) {
    if (COMBINED_COMPARERS.includes(state.comparer) && hasValue) {
      disableApply = false;
    } else if (['yesterday', 'today'].includes(state.comparer)) {
      disableApply = false;
    }
  }

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.date.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.field && (
        <Filters.ListItem
          heading
          label={t('applicants.filters.date.' + state.field)}
          icon={<IconArrowLeft />}
          onClick={actions.unselectField}
        />
      )}

      {state.comparer && ABSOLUTE_COMPARERS.includes(state.comparer) && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer)}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {state.comparer && ABSOLUTE_COMPARERS.includes(state.comparer) && (
        <DatePicker
          lang={lang}
          value={hasValue ? new Date(state.value as string) : ''}
          onChange={(val: Date) => actions.selectValue(val.toISOString())}
          inline
        />
      )}

      {!state.field && (
        <Fragment>
          {DATE_FIELDS.map((field) => (
            <Filters.ListItem
              key={field}
              label={t('applicants.filters.date.' + field)}
              onClick={() => actions.selectField(field)}
            />
          ))}
        </Fragment>
      )}

      {state.field && !(state.comparer && ABSOLUTE_COMPARERS.includes(state.comparer)) && (
        <Fragment>
          <Filters.SubHeading text={t('applicants.filters.date.rel')} />

          <Filters.ListItem
            icon={state.comparer === 'today' ? <IconRadioChecked /> : <IconRadioIdle />}
            label={t('comparers.today')}
            onClick={() => actions.selectComparer('today')}
          />

          <Filters.ListItem
            icon={state.comparer === 'yesterday' ? <IconRadioChecked /> : <IconRadioIdle />}
            label={t('comparers.yesterday')}
            onClick={() => actions.selectComparer('yesterday')}
          />
          {RELATIVE_COMPARERS.map((comparer) => (
            <Fragment key={comparer}>
              <Filters.ListItem
                icon={state.comparer === comparer ? <IconRadioChecked /> : <IconRadioIdle />}
                label={t('comparers.' + comparer)}
                onClick={() => actions.selectComparer(comparer)}
              />
              {state.comparer === comparer && (
                <Stack space={2} orientation="horizontal" align="center">
                  <TextField
                    autoFocus
                    hideLabel
                    label={t('comparers.' + comparer)}
                    value={state.value as string}
                    onChange={(val) => {
                      if (!val || /^\d+$/.test(val)) {
                        actions.selectValue(val);
                      }
                    }}
                  />
                  <Text font="regular14" color="purple80" text={t('filters.daysago')} />
                </Stack>
              )}
            </Fragment>
          ))}

          <Filters.SubHeading text={t('applicants.filters.date.abs')} />

          {ABSOLUTE_COMPARERS.map((comparer) => (
            <Filters.ListItem
              key={comparer}
              label={t('comparers.' + comparer)}
              onClick={() => actions.selectComparer(comparer)}
            />
          ))}
        </Fragment>
      )}

      {state.field && <FilterActionButtons disableApply={disableApply} />}
    </Filters.List>
  );
}

export function useFilterApplicationDates(): FilterConfig {
  const { t } = useStore();

  return useMemo(
    () => ({
      category: 'dates',
      label: t('applicants.filters.date.title'),
      icon: <IconDate />,
      AppliedFilter: FilterApplicationDatesApplied,
      Menu: FilterApplicationDatesMenu,
      toQueryData: (data) => {
        const now = new Date();
        const prefix = datePrefix(data.field!);

        switch (data.comparer) {
          case 'today': {
            const selectedDay = new Date();
            return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
          }
          case 'yesterday': {
            const selectedDay = subDays(now, 1);
            return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
          }
          case 'gt': {
            const xDaysAgo = differenceInMinutes(now, subDays(now, Number(data.value)));
            return { [`${prefix}__lt`]: `now-${xDaysAgo}m` };
          }
          case 'eq': {
            const selectedDay = subDays(now, Number(data.value));
            return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
          }
          case 'lt': {
            const xDaysAgo = differenceInMinutes(now, subDays(now, Number(data.value)));
            return { [`${prefix}__gt`]: `now-${xDaysAgo}m` };
          }
          case 'after':
            return { [`${prefix}__gte`]: data.value };
          case 'before':
            return { [`${prefix}__lt`]: data.value };
          case 'on': {
            const selectedDay = new Date(data.value as string);
            return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
          }
          default:
            return {};
        }
      },
    }),
    [t]
  );
}

function datePrefix(field: (typeof DATE_FIELDS)[number]) {
  switch (field) {
    case 'hired':
      return 'date_of_hire';
    case 'rejected':
      return 'date_of_rejection';
    default:
      return 'date';
  }
}
